import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiAccountCircle,
  mdiBrightness4,
  mdiBrightness7,
  mdiLogout,
  mdiMenu,
  mdiStore,
} from "@mdi/js";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
  useColorScheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import devTag from "../../../../images/dev-tag.png";
import equinorLogoHorizontal from "../../../../images/equinor-logo-horizontal.svg";
import homologTag from "../../../../images/homolog-tag.png";
import { useAuth } from "../../../hooks/useAuth";
import { useTopMenu } from "../../../hooks/useTopMenu";
import { HybridRouteButtonLink } from "./HybridRouteButtonLink";
import { Notifications } from "./Notifications";
import { ReleaseInfos } from "./ReleaseInfos";
import { EnvTag, Logo } from "./styles";

export const TopMenu: React.FC<{
  setLeftMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setLeftMenuOpen }) => {
  const { t } = useTranslation();

  // TODO: Uncomment when all routes in React
  // const router = useRouter();
  // const routeApi = getRouteApi("__root__");
  // const navigate = routeApi.useNavigate();

  const { user, signOut } = useAuth();
  const {
    mode: themeMode,
    systemMode: themeSystemMode,
    setMode,
  } = useColorScheme();

  const { setTopMenuHeight, setTopMenuWidth } = useTopMenu();
  const ref = useRef<HTMLDivElement>(null);

  const [envTag] = useState(() => {
    if (window.environment === "development") {
      return devTag;
    }
    if (window.environment === "homolog") {
      return homologTag;
    }
    return null;
  });
  const [loadingLogout, setLoadingLogout] = useState(false);

  // const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = false;

  const handleLeftMenu = useCallback(() => {
    setLeftMenuOpen((currentValue) => !currentValue);
  }, [setLeftMenuOpen]);

  const handleLogout = useCallback(() => {
    setLoadingLogout(true);
    signOut().then(() => {
      // TODO: Uncomment when all routes in React
      // router.invalidate().finally(() => {
      //   navigate({ to: "/" });
      // });

      // TODO: Remove after all routes in React
      window.location.href = "/";
    });
  }, [signOut]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      setTopMenuHeight(element.offsetHeight);
      setTopMenuWidth(element.offsetWidth);
    });

    resizeObserver.observe(element);

    setTopMenuHeight(element.offsetHeight);
    setTopMenuWidth(element.offsetWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setTopMenuHeight, setTopMenuWidth]);

  return (
    <AppBar
      color="white"
      elevation={0}
      position="sticky"
      ref={ref}
      sx={(currentTheme) => ({
        borderBottom: `1px solid ${currentTheme.vars.palette.divider}`,
      })}
    >
      <Box
        component="main"
        sx={{
          columnGap: 1, // Only horizontal gap
          display: "grid",
          flexGrow: 1,
          gridTemplateAreas: `"logo . announcement_modal header"`,
          gridTemplateColumns: "repeat(3, 1fr)",
          gridTemplateRows: "auto",
          justifyContent: "center",
          rowGap: 0, // No vertical gap
        }}
      >
        <Toolbar
          sx={{
            gridArea: "logo",
            height: (currentTheme) => currentTheme.mixins.toolbar.minHeight,
          }}
        >
          {isMobile ? (
            <IconButton
              aria-label="open drawer"
              color="inherit"
              edge="start"
              onClick={handleLeftMenu}
              sx={{ mr: 2 }}
            >
              <Icon icon={mdiMenu} />
            </IconButton>
          ) : (
            <>
              {!!envTag && <EnvTag src={envTag} />}
              <Logo
                alt="Equinor logo"
                src={`${equinorLogoHorizontal}#${(themeSystemMode ?? themeMode) === "dark" ? "white" : "red"}`}
              />
            </>
          )}
        </Toolbar>
        <Box
          sx={{
            alignItems: "center",
            display: "grid",
            gridArea: "announcement_modal",
            justifyContent: "center",
          }}
        ></Box>
        <Box
          sx={{
            alignItems: "center",
            display: "grid",
            gap: 2,
            gridArea: "header",
            gridTemplateAreas: `"release_infos notifications mode account"`,
            gridTemplateColumns: "auto auto auto",
            gridTemplateRows: "auto",
            justifyContent: "center",
            marginRight: 1,
          }}
        >
          <ReleaseInfos />
          <Notifications />

          <IconButton
            color="slate"
            onClick={() => setMode(themeMode === "dark" ? "light" : "dark")}
            sx={{ gridArea: "mode", ml: 1 }}
          >
            {themeMode === "dark" ? (
              <Icon icon={mdiBrightness7} />
            ) : (
              <Icon icon={mdiBrightness4} />
            )}
          </IconButton>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography color="slate">{user?.full_name_with_login}</Typography>
            <Box
              component="main"
              sx={{
                display: "grid",
                flexGrow: 1,
                gap: 1,
                gridArea: "actions",
                gridTemplateAreas: user?.vendor
                  ? `"my_account my_vendor sign_out"`
                  : `"my_account sign_out"`,
                gridTemplateColumns: "repeat(2, 1fr)",
                gridTemplateRows: "auto",
                justifyContent: "center",
              }}
            >
              {!!user && (
                <>
                  <HybridRouteButtonLink
                    color="slate"
                    size="small"
                    startIcon={
                      user.equinor && user.has_photo && user.photo_url ? (
                        <Avatar
                          alt={user.full_name}
                          src={user.photo_url}
                          sx={{ height: 24, width: 24 }}
                        >
                          <Icon icon={mdiAccountCircle} />
                        </Avatar>
                      ) : (
                        <Icon icon={mdiAccountCircle} />
                      )
                    }
                    sx={{
                      gridArea: "my_account",
                      textTransform: "capitalize",
                      width: "100%",
                    }}
                    url="/users/edit"
                    variant="text"
                  >
                    {t("topMenu.myAccount")}
                  </HybridRouteButtonLink>

                  {!!user?.vendor && (
                    <HybridRouteButtonLink
                      color="slate"
                      size="small"
                      startIcon={<Icon icon={mdiStore} />}
                      sx={{
                        gridArea: "my_vendor",
                        textTransform: "capitalize",
                        width: "100%",
                      }}
                      url="/vsupplierregistration/vendor"
                      variant="text"
                    >
                      {t("topMenu.myVendor")}
                    </HybridRouteButtonLink>
                  )}

                  <Button
                    color="slate"
                    loading={loadingLogout}
                    onClick={handleLogout}
                    size="small"
                    startIcon={<Icon icon={mdiLogout} />}
                    sx={{
                      gridArea: "sign_out",
                      textTransform: "capitalize",
                      width: "100%",
                    }}
                    variant="text"
                  >
                    {t("topMenu.signOut")}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </AppBar>
  );
};
